import { useMutation } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useShowAlert } from '~/composables/use-show-alert'
import { useSetCartData } from '~/composables/use-cart'
import type { CreateGiftCardInput } from '~/resources/graphql/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'

export function useGiftCardCreateContractMutation() {
    const query = useRpQuery()
    const selectedLanguage = useSelectedLanguage()
    const activeFacility = useActiveFacility()
    const showAlert = useShowAlert()
    const setCartData = useSetCartData()

    return useMutation({
        mutationFn: async (mutationData: CreateGiftCardInput) => {
            const response = await query(
                graphql(/** @lang GraphQL */ `
                    mutation GiftCardCreateContractMutation(
                        $input: CreateGiftCardInput!
                        $facilityId: ID!
                        $language: Language!
                    ) {
                        createGiftCard(contractInput: $input) {
                            __typename
                            ... on CreateGiftCardResult {
                                recordId
                                record {
                                    __typename
                                    ... on PlanContract {
                                        ...PurchaseGiftCardContractFragment
                                    }
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id,
                    language: selectedLanguage.value
                }
            )

            if (response.createGiftCard.__typename === 'CreateGiftCardResult') {
                // Update cart
                if (response.createGiftCard.query.viewer !== null) {
                    setCartData(response.createGiftCard.query.viewer.cart)
                }

                if (
                    response.createGiftCard.record.__typename === 'PlanContract'
                ) {
                    return response.createGiftCard.record
                } else {
                    throw new Error()
                }
            }

            if (response.createGiftCard.fieldErrors.length > 0) {
                await showAlert(
                    'Heads Up!',
                    response.createGiftCard.fieldErrors[0].message
                )
            }

            throw new Error()
        }
    })
}
